import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  IconButton,
  Button,
  FormControl,
  FormLabel,
  VStack,
} from "@chakra-ui/react";
import { MdDelete, MdSearch, MdEdit } from "react-icons/md";

import Page from "../components/common/Page";
import { ApiCommand } from "../lib/Api";
import useToastNotification from "../lib/hooks/useToastNotification";
import StrongTable from "../components/common/StrongTable";
import StrongPagination from "../components/common/StrongPagination";
import useApi from "../lib/hooks/useApi";
import urlConstants from "../lib/constants/url.constants";
import EditProgramInfoModal from "../components/programInfo/EditProgramInfoModal";
import StrongInput from "../components/common/StrongInput";
import ThumbnailPreview from "../components/common/ThumbnailPreview";
import { useParams } from "react-router-dom";
import { GetProgramResopnse, Week } from "../lib/types/programEdit";
import { WeekPlan } from "../components/programInfo/WeekPlan";
import { AddIcon } from "@chakra-ui/icons";
import { Path } from "../lib/constants/path.constants";
import { useNavigate } from "react-router-dom";
import { MdSettings } from "react-icons/md"; // added by jitendra (18-04-2026)                      
import ProgramSettingsModal from "../components/programInfo/ProgramSettingsModal"; // added by jitendra (18-04-2026) 
const { getProgram: getProgramUrl, editSection: editSectionUrl } =
  urlConstants.programInfo;
const { getEquipmentstitle: getEquipmentsTitleUrl } =
  urlConstants.exercises;                                                                 
  const { getResourcesTitle: getResourcesTitleUrl } =         
  urlConstants.resources;
const normalizeIdValue = (value: any): string => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    return value._id || value.id || "";
  }
  return "";
};

const ProgramEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState<GetProgramResopnse>({
    _id: "",
    title: "",
    pid: "",
    description: "",
    short_description: "",
    type: "",
    duration: "",
    cover: "",
    thumbnail: "",
    introVimeoId: "", 
    purchaseLink: "",
    equipment: [],
    resources: [],
    nutrition: "",
    isFeatured: false,
    woocommerceVariationId: "", // added by jitendra (18-04-2026)
    weeks: [],
    genders: [],
  });
  const showToast = useToastNotification();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
   const [coverFile, setCoverFile] = useState<File | null>(null); 
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null); 

  const [equipmentstitle, setEquipmentsTitle] =               
  useState<EquipmentTitle[]>([]);              
  const [resourcestitle, setResourcesTitle] =                 
  useState<ResourceTitle[]>([]); 
  
  const [dayThumbnailFiles,setDayThumbnailFiles] =  useState<Record<string, File | null>>({}); 
                                            
  const handleDayThumbnailFile = (weekIndex: number, dayIndex: number, file: File | null) => {                             
    setDayThumbnailFiles(prev => ({ ...prev, [`${weekIndex}_${dayIndex}`]: file }));   
  };        

  const { loading: isFetchProgramLoading, sendRequest: getProgram } =
    useApi<GetProgramResopnse>();

  const { loading: isEditProgramLoading, sendRequest: editProgram } =
    useApi<any>();

  const { sendRequest: getEquipmentstitle } =                 
  useApi<EquipmentTitle[]>();                               
  const { sendRequest: getResourcestitle } =                  
    useApi<ResourceTitle[]>();
  
  const fetchSections = (programId: string) => {
    getProgram({
      callback: (data: GetProgramResopnse | null, error: string | null) => {
        if (error) {
          showToast({
            title: "Failed",
            description: error,
            status: "error",
          });
          return;
        }
        if (!data) return null;
        console.log('ProgramEdit: fetched program details:', data);
        setProgram({
          ...data,
          // equipment: data.equipment ?? [],
          // resources: data.resources ?? [],

          equipment: (data.equipment ?? [])
            .map((item: any) =>
              typeof item === "string" ? item : item?._id || item?.id,
            )
            .filter(Boolean),
          resources: (data.resources ?? [])
            .map((item: any) =>
              typeof item === "string" ? item : item?._id || item?.id,
            )
            .filter(Boolean),
          weeks:
            data.weeks?.map((week) => ({
              ...week,
              days:
                week.days?.map((day) => ({
                  ...day,
                  restDayId:
                    typeof day.restDayId === "string" || !day.restDayId
                      ? day.restDayId ?? null
                      : day.restDayId._id || null,
                  exercises:
                    day.exercises?.map((exercise) => ({
                      ...exercise,
                      exerciseId: normalizeIdValue(exercise.exerciseId),
                    })) ?? [],
                  circuits:
                    day.circuits?.map((circuit) => ({
                      ...circuit,
                      circuitExercises:
                        circuit.circuitExercises?.map((exercise) => ({
                          ...exercise,
                          exerciseId: normalizeIdValue(exercise.exerciseId),
                        })) ?? [],
                    })) ?? [],
                  warmups:
                    day.warmups?.map((warmup) => ({
                      ...warmup,
                      circuitExercises:
                        warmup.circuitExercises?.map((exercise) => ({
                          ...exercise,
                          exerciseId: normalizeIdValue(exercise.exerciseId),
                        })) ?? [],
                    })) ?? [],
                })) ?? [],
            })) ?? [],
        });
      },
      command: ApiCommand.GET,
      url: getProgramUrl.replace(":id", programId),
    });
  };

  const fetchEquipmentsTitles = () => {
    getEquipmentstitle({               
      callback: (data, error) => {                            
        if (data) setEquipmentsTitle(data);
      },                                                      
      command: ApiCommand.GET,                                
      url: getEquipmentsTitleUrl,
    });                                                       
  };                                                        
    
  const fetchResourcesTitles = () => {
    getResourcestitle({               
      callback: (data, error) => {
        if (data) setResourcesTitle(data);                    
      },                                  
      command: ApiCommand.GET,                                
      url: getResourcesTitleUrl,                            
    });                         
  };

  const addWeek = () => {
    const newWeek: Week = {
      title: "New Week",
      description: "",
      vimeoId: "",
      thumbnail: "",
      days: [],
    };

    const updatedProgram = {
      ...program,
      weeks: [...(program.weeks || []), newWeek],
    };

    updateProgram(updatedProgram);
  };

  const updateProgram = (updatedProgram: GetProgramResopnse) => {
    setProgram((prev) => {
      const mergedProgram: GetProgramResopnse = {
        ...prev,
        ...updatedProgram,
        equipment: updatedProgram.equipment ?? prev.equipment ?? [],
        resources: updatedProgram.resources ?? prev.resources ?? [],
        genders: updatedProgram.genders ?? prev.genders ?? [],
        weeks: updatedProgram.weeks ?? prev.weeks ?? [],
      };

      const weeksCount = mergedProgram.weeks?.length ?? 0;
      mergedProgram.duration =
        mergedProgram.type === "ongoing" ? " " : String(weeksCount);

      return mergedProgram;
    });
  };

  const onSetProgramTitle = (title: string) => {
    updateProgram({ ...program, title });
  };

  const onSaveProgram = () => {
    console.log('onSaveProgram called with program:', program);
    console.log('Program genders:', program.genders);
    let payload: any;
    const hasDayFiles = Object.values(dayThumbnailFiles).some(f => f !== null);
    if (thumbnailFile || coverFile || hasDayFiles) {
      const formData = new FormData();
      // Ensure purchaseLink is present inside the program JSON we send
      const programToSend = { ...program, purchaseLink: program.purchaseLink ?? "" };
      formData.append("program", JSON.stringify(programToSend));
      console.log('Sending program as JSON:', JSON.stringify(programToSend).substring(0, 500));
      if (thumbnailFile) formData.append("thumbnail", thumbnailFile);
      if (coverFile) formData.append("cover", coverFile);

      Object.entries(dayThumbnailFiles).forEach(([key, file]) => {
        if (file) formData.append(`dayThumbnail_${key}`, file);
      });
      formData.append("short_description", programToSend.short_description ?? "");
      formData.append("description", programToSend.description ?? "");
      formData.append("purchaseLink", programToSend.purchaseLink ?? "");
      payload = formData;
    } else {
      const programToSend = { ...program, purchaseLink: program.purchaseLink ?? "" };
      console.log('No files, sending program as JSON object:', programToSend);
      // send the program payload wrapped under `program` so backend update handler can parse it
      payload = {
        program: JSON.stringify(programToSend),
      };
    }
    editProgram({
      callback: (_data, error: string | null) => {
        if (error) {
          showToast({
            title: "Failed",
            description: "Please set exercise value",
            status: "error",
          });
          return;
        }
        showToast({
          title: "Success",
          //TODO create toast.constants.ts file and create variables for all toast description
          description: "Program updated successfully",
          status: "success",
        });
        // setIsOpenModal(false);
        // fetchExercises();
        navigate(Path.PROGRAM_INFO);
      },
      command: ApiCommand.PUT,
      url: editSectionUrl,
      options: payload ,
    });
  };

  useEffect(() => {
    fetchSections(id ? id : "0");
    fetchEquipmentsTitles();
    fetchResourcesTitles();
  }, [id]);

  // useEffect(() => {
  //   fetchEquipmentsTitles();
  //   fetchResourcesTitles();
  // }, []);

  return (
    <Page>
      <Flex
        justifyContent="end"
        alignItems="center"
        mb={6}
        gap={4}
        flexWrap="wrap"
      >
        <Box flex="1" maxW="400px"></Box>
        {/* add by jitendra (18-04-2026) */}
        <IconButton
          aria-label="Program Settings"                              
          icon={<MdSettings />}                                        
          variant="outline"             
          colorScheme="primary"                                        
          onClick={() => setIsSettingsOpen(true)}
        /> 
        {/* add by jitendra (18-04-2026) */}
        <Button
          colorScheme="primary"
          px={6}
          py={4}
          borderRadius="md"
          fontWeight="bold"
          _hover={{ bg: "primary.300" }}
          onClick={() => {
            onSaveProgram();
          }}
        >
          Save Program
        </Button>
      </Flex>

      <Flex flexDirection="column" height="full" justifyContent="space-between">
        <Box
          backgroundColor="white"
          borderRadius="lg"
          height="calc(100vh - 170px)"
          overflowY="auto"
        >
          <VStack px={10} py={10} align="stretch">
            <FormControl>
              <FormLabel color="gray.600">Title</FormLabel>
              <StrongInput
                placeholder="Enter title"
                value={program?.title}
                onChange={(e) => onSetProgramTitle(e.target.value)}
              />
            </FormControl>
            <FormControl>
              {program?.weeks.map((week, weekIndex) => (
                <WeekPlan
                  key={weekIndex}
                  weekIndex={weekIndex}
                  week={week}
                  program={program}
                  updateProgram={updateProgram}
                  onDayThumbnailFile={handleDayThumbnailFile}
                />
              ))}

              <Button
                colorScheme="primary"
                leftIcon={<AddIcon />}
                onClick={() => addWeek()}
              >
                Add Week
              </Button>
            </FormControl>
          </VStack>
        </Box>
      </Flex>
      {/* Program Settings Modal */}
      {/* add by jitendra (18-04-2026) to add woocommerce variation id in program info and program edit page. */}
      <ProgramSettingsModal
        isOpen={isSettingsOpen}                                  
        onClose={() => setIsSettingsOpen(false)}
        woocommerceVariationId={program.woocommerceVariationId ??
          ""}
        purchaseLink={program.purchaseLink ?? ""}
        shortDescription={program.short_description ?? ""}
        description={program.description ?? ""}
        cover={program.cover ?? ""} // added by jitendra (18-04-2026)
        thumbnail={program.thumbnail ?? ""}
        introVimeoId={program.introVimeoId ?? ""} // added by jitendra (18-04-2026)
        // onSave={(variationId, coverFile, thumbnailFile, shortDescription, description, introVimeoId) => {                                 
        //   updateProgram({ ...program, woocommerceVariationId: variationId, short_description: shortDescription, description: description, introVimeoId: introVimeoId });
        //   if (coverFile) setCoverFile(coverFile);
        //   if (thumbnailFile) setThumbnailFile(thumbnailFile);
        // }}
        equipmentstitle={equipmentstitle}                         
        resourcestitle={resourcestitle}                         
        selectedequipments={program.equipment ?? []}
        selectedresources={program.resources ?? []} 
        selectedgenders={program.genders ?? []}
        isFeatured={program.isFeatured ?? false}
        onSave={(
          variationId,
          purchaseLink,
          coverFile,
          thumbnailFile,
          shortDescription,
          description,
          introVimeoId,
          selectedequipments,
          selectedresources,
          selectedgenders,
          isFeatured,
          coverRemoved,
          thumbnailRemoved,
        ) => {
          updateProgram({
            ...program,
            cover: coverRemoved ? "" : program.cover,
            thumbnail: thumbnailRemoved ? "" : program.thumbnail,
            woocommerceVariationId: variationId,
            purchaseLink,
            short_description: shortDescription,
            description,
            introVimeoId,
            equipment: selectedequipments,
            resources: selectedresources,
            genders: selectedgenders ?? program.genders ?? [],
            isFeatured: isFeatured ?? program.isFeatured ?? false,
          });
          setCoverFile(coverRemoved ? null : coverFile);
          setThumbnailFile(thumbnailRemoved ? null : thumbnailFile);
        }}
      />   
      {/* add by jitendra (18-04-2026) to add woocommerce variation id in program info and program edit page. */}
    </Page>
  );
};

export default ProgramEdit;
